import { Icon } from "@blueprintjs/core";
import classNames from "classnames";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { Box, Flex } from "@/components/layout/flexbox";
import { StandaloneCheckbox } from "@/components/pieces/form/StandaloneCheckbox";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import type { HeaderConfig } from "@/types/tables";

interface HeaderProps<C extends HeaderConfig> {
  config: C;
  currentSort: { field?: C["field"]; asc?: boolean };
  onSort: (field: C["field"]) => void;
  checkedAll?: boolean;
  onCheckAll?: (checked: boolean) => void;
}

export function Header<C extends HeaderConfig>({
  config,
  currentSort,
  onSort,
  checkedAll,
  onCheckAll,
}: HeaderProps<C>) {
  const field = config.field;
  const isActive = currentSort.field === field;
  const justifyContent = config.justifyContent || "flex-start";

  let target = config.isNotSortable ? (
    <Flex alignItems="center" justifyContent={justifyContent}>
      <Box>{config.title}</Box>
    </Flex>
  ) : (
    <Clickable onClick={() => onSort(field)}>
      <Flex alignItems="center" justifyContent={justifyContent}>
        <Box mr={1}>{config.title}</Box>
        <Icon
          icon={
            isActive
              ? currentSort.asc
                ? "chevron-up"
                : "chevron-down"
              : "chevron-down"
          }
        />
      </Flex>
    </Clickable>
  );

  if (config.type === "checkbox") {
    target = (
      <StandaloneCheckbox
        checked={checkedAll}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onCheckAll?.(e.target.checked);
        }}
      />
    );
  }

  return (
    <th
      className={classNames("header", {
        "active-sort": isActive,
      })}
      style={{
        maxWidth: config.maxWidth || "auto",
        border: "none",
      }}
      data-test-id={`program-${config.field}-column`}
    >
      {config.tooltip ? (
        <Tooltip
          content={<div style={{ maxWidth: 200 }}>{config.tooltip}</div>}
          placement="top-end"
          minimal={true}
        >
          {target}
        </Tooltip>
      ) : (
        <> {target} </>
      )}
    </th>
  );
}
